import { useEffect, useRef, useCallback } from "react";
import { Bot } from "lucide-react";
import { type Message, MessageBubble } from "./MessageBubble";
import { TypingIndicator } from "./TypingIndicator";

import { type FeedbackRating } from "./FeedbackBar";

interface MessageListProps {
	messages: Message[];
	isLoading?: boolean;
	onFeedbackSubmit?: (messageId: string, rating: FeedbackRating, comment: string) => void;
	onGuidanceSelect?: (selection: { label: string; query?: string }) => void;
	/** If provided, persist scrollTop across remounts/navigation (e.g., widget pages) */
	persistScrollKey?: string;
}

const scrollStorageKey = (key: string) => `shirobon_ai_widget_scroll:${key}`;
const anchorStorageKey = (key: string) => `shirobon_ai_widget_anchor:${key}`;

const safeLoadNumber = (key: string): number | null => {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.sessionStorage.getItem(key);
		if (!raw) return null;
		const n = Number(raw);
		return Number.isFinite(n) ? n : null;
	} catch {
		return null;
	}
};

const safeSaveNumber = (key: string, value: number) => {
	if (typeof window === "undefined") return;
	try {
		window.sessionStorage.setItem(key, String(value));
	} catch {
		// ignore
	}
};

const safeLoadString = (key: string): string | null => {
	if (typeof window === "undefined") return null;
	try {
		return window.sessionStorage.getItem(key);
	} catch {
		return null;
	}
};

const safeSaveString = (key: string, value: string) => {
	if (typeof window === "undefined") return;
	try {
		window.sessionStorage.setItem(key, value);
	} catch {
		// ignore
	}
};

export function MessageList({ messages, isLoading, onFeedbackSubmit, onGuidanceSelect, persistScrollKey }: MessageListProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const bottomRef = useRef<HTMLDivElement>(null);
	const isAtBottomRef = useRef(true);
	const prevLenRef = useRef<number>(0);
	const didRestoreScrollRef = useRef(false);
	const hasSavedScrollRef = useRef(false);

	const recomputeAtBottom = useCallback(() => {
		const el = containerRef.current;
		if (!el) return true;
		const thresholdPx = 80;
		const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
		return distance <= thresholdPx;
	}, []);

	const handleScroll = useCallback(() => {
		const atBottom = recomputeAtBottom();
		isAtBottomRef.current = atBottom;
		if (persistScrollKey && containerRef.current) {
			safeSaveNumber(scrollStorageKey(persistScrollKey), containerRef.current.scrollTop);
			hasSavedScrollRef.current = true;
		}
	}, [recomputeAtBottom]);

	useEffect(() => {
		// Restore scroll position on mount (for widget navigation).
		if (didRestoreScrollRef.current) return;
		if (!persistScrollKey) return;
		const el = containerRef.current;
		if (!el) return;

		// Prefer anchoring to the last user prompt (ChatGPT-like) over raw scrollTop.
		// This avoids jumping back to the very top ("welcome") when the list remounts.
		const anchorId = safeLoadString(anchorStorageKey(persistScrollKey));
		const saved = safeLoadNumber(scrollStorageKey(persistScrollKey));

		// Mark as restored immediately so the initial auto-scroll effect won't run.
		didRestoreScrollRef.current = true;
		hasSavedScrollRef.current = true;

		requestAnimationFrame(() => {
			const node = containerRef.current;
			if (!node) return;

			if (anchorId) {
				const target = node.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(anchorId)}"]`);
				if (target) {
					target.scrollIntoView({ behavior: "auto", block: "start" });
					isAtBottomRef.current = recomputeAtBottom();
					return;
				}
			}

			if (saved != null) {
				node.scrollTop = saved;
				isAtBottomRef.current = recomputeAtBottom();
			}
		});
	}, [persistScrollKey, recomputeAtBottom]);

	useEffect(() => {
		const len = messages.length;
		const prevLen = prevLenRef.current;
		prevLenRef.current = len;

		// If we restored scroll, do not force-jump on mount.
		if (prevLen === 0 && len > 0 && (didRestoreScrollRef.current || hasSavedScrollRef.current)) {
			return;
		}

		// Initial mount (no restored scroll): keep the latest visible.
		if (prevLen === 0 && len > 0) {
			bottomRef.current?.scrollIntoView({ behavior: "auto" });
			isAtBottomRef.current = true;
			return;
		}

		const lastRole = messages[len - 1]?.role;
		const atBottom = recomputeAtBottom();

		// Desired behavior:
		// - When the user sends a message, keep them at the bottom (user input line visible).
		// - When the AI response arrives, DO NOT auto-scroll; keep the user's message position.
		// - If the user is reading older messages, never auto-scroll and keep offering the jump button.
		if (lastRole === "user") {
			if (atBottom) {
				bottomRef.current?.scrollIntoView({ behavior: "smooth" });
				isAtBottomRef.current = true;
			}
			return;
		}

		// AI message (or loading indicator update)
		isAtBottomRef.current = atBottom;
	}, [messages, isLoading]);

	// Persist scrollTop even if the user never manually scrolls (critical for navigation)
	useEffect(() => {
		if (!persistScrollKey || !containerRef.current) return;
		const key = scrollStorageKey(persistScrollKey);
		safeSaveNumber(key, containerRef.current.scrollTop);
		hasSavedScrollRef.current = true;
		return () => {
			if (!containerRef.current) return;
			safeSaveNumber(key, containerRef.current.scrollTop);
		};
	}, [persistScrollKey, messages, isLoading]);

	// Persist "last user prompt" anchor so navigation restores near the latest user message.
	useEffect(() => {
		if (!persistScrollKey) return;
		const lastUser = [...messages].reverse().find((m) => m.role === "user");
		if (!lastUser) return;
		safeSaveString(anchorStorageKey(persistScrollKey), lastUser.id);
	}, [persistScrollKey, messages]);

	return (
		<div
			ref={containerRef}
			onScroll={handleScroll}
			className="relative flex flex-1 flex-col gap-2 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/70 p-2"
		>
			{messages.map((message) => (
				<div key={message.id} data-message-id={message.id} data-role={message.role}>
					<MessageBubble
						message={message}
						onFeedbackSubmit={onFeedbackSubmit}
						onGuidanceSelect={onGuidanceSelect}
					/>
				</div>
			))}
			{isLoading && (
				<div className="flex w-full gap-3 p-2 justify-start">
					<div className="flex max-w-[80%] flex-col gap-2 items-start">
						<div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400">
							<span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500">
								<Bot className="h-5 w-5" />
							</span>
							<span>しろぼんAI</span>
						</div>
						<div className="rounded-2xl border border-emerald-200 bg-white p-4 text-sm leading-relaxed shadow-sm shadow-emerald-100/70">
							<TypingIndicator variant="assistant" />
						</div>
					</div>
				</div>
			)}
			<div ref={bottomRef} />
		</div>
	);
}
